/** @format */

import React from 'react';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useAppSelector } from '../../../../app/hooks';
import CircularLoader from '../../../../common/loaders/circularLoader';
import { selectConstants } from '../../../../common/dropdowns/Api/constantSlice';
import { DropdownDto } from '../../../../common/dropdowns/Api/Models';
import { selectApplicant } from '../../ApplicantSlice';
import { ApplicationDetailsDto } from '../../Models';
import StudyInfo from './StudyInfo';

interface Props {
  applicant: ApplicationDetailsDto | null;
}

function ProfileLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'text.secondary',
        display: 'block',
        mb: 0.5,
      }}>
      {children}
    </Typography>
  );
}

function ProfileValue({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
      {children}
    </Typography>
  );
}

export default function InstitutionInfo({ applicant }: Props) {
  const constantsSelect = useAppSelector(selectConstants);
  const applicantSelect = useAppSelector(selectApplicant);

  const getInstitution = (key: number[]) => {
    const array: DropdownDto[] = [];
    if (!constantsSelect.constantState?.universities) return array;

    for (let x = 0; x < key.length; x++) {
      const val = key[x];
      const findInstitution = constantsSelect.constantState.universities.find(
        (j) => j.id === val
      );
      if (findInstitution) {
        array.push(findInstitution);
      }
    }
    return array;
  };

  const getTypeOfStudy = (key: number | string) => {
    const findTypeOfStudy = constantsSelect.constantState?.typeOfStudies.find(
      (x) => x.id === key
    );
    return findTypeOfStudy?.description;
  };

  const getYearOfStudy = (key: number | string) => {
    const findYearOfStudy = constantsSelect.constantState?.yearOfStudies.find(
      (x) => x.id === key
    );
    return findYearOfStudy?.description;
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
      }}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
        <CircularLoader status={applicantSelect.loginState.status} />

        {applicant?.studyDetailsDto && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: (t) =>
                      t.palette.mode === 'light'
                        ? 'rgba(25, 118, 210, 0.08)'
                        : 'action.selected',
                    color: 'primary.main',
                  }}>
                  <SchoolOutlined fontSize="small" />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Study application
                </Typography>
              </Stack>

              <Stack spacing={2.5}>
                <Box>
                  <ProfileLabel>Qualification start year</ProfileLabel>
                  <ProfileValue>
                    {applicant.studyDetailsDto.qualificationStartYear}
                  </ProfileValue>
                </Box>
                <Box>
                  <ProfileLabel>Type of study</ProfileLabel>
                  <ProfileValue>
                    {getTypeOfStudy(applicant.studyDetailsDto.typeOfStudyId)}
                  </ProfileValue>
                </Box>
                <Box>
                  <ProfileLabel>Year of study</ProfileLabel>
                  <ProfileValue>
                    {getYearOfStudy(applicant.studyDetailsDto.yearOfStudyId)}
                  </ProfileValue>
                </Box>
                <Box>
                  <ProfileLabel>Institution of choice</ProfileLabel>
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                    {getInstitution(
                      applicant.studyDetailsDto.institutions as unknown as number[]
                    ).map((desc) => (
                      <Chip
                        key={desc.id}
                        label={desc.description}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          borderRadius: 2,
                          borderLeft: '3px solid',
                          borderColor: 'primary.main',
                          bgcolor: (t) =>
                            t.palette.mode === 'light'
                              ? 'rgba(25, 118, 210, 0.06)'
                              : 'action.hover',
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{
                borderLeft: { md: '1px solid' },
                borderTop: { xs: '1px solid', md: 'none' },
                borderColor: 'divider',
                pt: { xs: 3, md: 0 },
                pl: { md: 3 },
                mt: { xs: 1, md: 0 },
              }}>
              {applicant.education && (
                <>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: (t) =>
                          t.palette.mode === 'light'
                            ? 'rgba(25, 118, 210, 0.08)'
                            : 'action.selected',
                        color: 'primary.main',
                      }}>
                      <MenuBookOutlined fontSize="small" />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      High school
                    </Typography>
                  </Stack>
                  <StudyInfo applicant={applicant} />
                </>
              )}
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}
